import React, { useEffect, useState } from "react";
import { Container, injectable } from "inversify";
import { container as mainContainer } from "../../../services";
import styles from "./CommandModals.module.scss";
import { IModalInstance } from "@whatslly/ui-components/modals/ModalProvider";
import { MessageModal } from "@whatslly/ui-components/modals/MessageModal";
import { ChatMessagesStore } from "@whatsllyorg/crm-components/ChatComponent/stores/ChatMessagesStore";
import { IChatMessagesDataLayer, ChatMessagesDataLayerSymbol, ComponentMessageType, ChatComponent } from "@whatsllyorg/crm-components/ChatComponent";
import { DependencyInjectionProvider, useDependencyInjector } from "@whatslly/commons/di";
import type { IUserStore } from "@whatslly/ui-components/services/UserStore";
import { InjectUserStore } from "@whatslly/ui-components/services/UserStore";
import { inject } from "inversify";
import { action, computed, makeObservable, observable, runInAction } from "mobx";
import { getJSON } from "@whatslly/commons/apiUtils";
import type { GetSidecarMessages } from "@whatslly/types/sidecars-server/SidecarMessages";
import type { IOrganizationStore } from "../../../services/IOrganizationStore";
import { InjectOrganizationStore } from "../../../services/IOrganizationStore";
import { stripNumberStringToOnlyNumbers } from "@whatslly/commons/utils";
import { observer } from "mobx-react-lite";
import { EntityFilter } from "@whatslly/ui-components/EntityFilter";
import { useTranslation } from "react-i18next";
import { GroupsTabStore } from "@whatsllyorg/crm-components/ChatComponent/stores/GroupsTabStore";
import { ChatGroupType, GroupMember } from "@whatsllyorg/crm-components/ChatGroupsComponent/types";
import uniqBy from "lodash/uniqBy";
import dayjs, { Dayjs } from "dayjs";
import type { DateFilterMonthsOptions } from "@whatsllyorg/crm-components/ChatComponent/types/DateFilterMonthsOptions";
import { ChatEntityFilterButton } from "@whatsllyorg/crm-components/ChatComponent/tabs/components/ChatEntityFilterButton";
import type { ReportedMessage } from "@whatslly/types/instant-messaging/messagesEmitter";
import { getChannelTypeToText } from "@whatslly/types/instant-messaging/messagesEmitter";
import { i18n } from "@whatslly/commons/i18nService";
import { getI18nLanguageFromUserSetting } from "@whatslly/commons/LanguageChangeUtils";

type GroupType = {
  Id: string;
  name: string;
  messages: ComponentMessageType[];
};
const startsWithLetter = (str: string) => /^[a-zA-Z]/.test(str);
const startsWithNumber = (str: string) => /^\d/.test(str);

type ViewMessageItem = { id?: string; phone: string[]; externalUserId?: string };

const sortByKey: <T extends string>(a: Record<T, string> & Record<string, any>, b: Record<T, string> & Record<string, any>, key: T) => number = (
  a,
  b,
  key
) => {
  const aStartsWithLetter = startsWithLetter(a[key]);
  const bStartsWithLetter = startsWithLetter(b[key]);
  const aStartsWithNumber = startsWithNumber(a[key]);
  const bStartsWithNumber = startsWithNumber(b[key]);

  if (aStartsWithLetter && !bStartsWithLetter) {
    return -1;
  } else if (!aStartsWithLetter && bStartsWithLetter) {
    return 1;
  } else if (!aStartsWithLetter && !bStartsWithLetter) {
    if (aStartsWithNumber && !bStartsWithNumber) {
      return 1;
    } else if (!aStartsWithNumber && bStartsWithNumber) {
      return -1;
    }
  }
  return a[key].localeCompare(b[key]);
};

@injectable()
class SidecarMessagesStore implements IChatMessagesDataLayer {
  baseUrl = "";
  item?: ViewMessageItem;
  destinationId?: string;
  messages: (ComponentMessageType & { groupName: string | null; chatId?: string })[] = [];
  selectedGroup?: GroupType;
  enableDatesFilter: boolean = false;
  private _selectedGroupFilters: Record<string, string[]> = {};
  private _filters: Record<string, string[]> = {};
  constructor(
    @inject(InjectUserStore) private readonly userStore: IUserStore,
    @inject(InjectOrganizationStore) private readonly orgStore: IOrganizationStore
  ) {
    makeObservable<SidecarMessagesStore, "_filters" | "_selectedGroupFilters" | "setSelectedGroup">(this, {
      item: observable.shallow,
      destinationId: observable,
      messages: observable,
      _filters: observable,
      _selectedGroupFilters: observable,
      getGroups: observable,
      getGroup: observable,
      enableDatesFilter: observable,
      filters: computed,
      userId: computed,
      init: action.bound,
      selectedGroupFilters: computed,
      selectedGroupFilterOptions: computed,
      selectedGroup: observable,
      setSelectedGroup: action,
    });
  }

  init(item: ViewMessageItem, destinationId: string, enableDatesFilter?: boolean) {
    this.item = item;
    this.destinationId = destinationId;
    this.messages = [];
    this.filters = {};
    this.enableDatesFilter = enableDatesFilter ?? false;
  }

  getCustomDefaultDateFilterOption() {
    return "1" as const;
  }

  private handleResetSelectedGroupOnDateFilterChange() {
    if (this.selectedGroup) {
      const currentSelectedGroupId = this.selectedGroup.Id;
      this.setSelectedGroup(undefined);
      this.getGroup("1", currentSelectedGroupId);
    }
  }

  async updateMessages(dateRange?: { fromDate: Dayjs | null; toDate: Dayjs | null }) {
    if (!this.item || !this.destinationId || (dateRange && (!dateRange.fromDate || !dateRange.toDate))) return [];
    const searchParams = new URLSearchParams({
      orgId: await this.orgStore.orgId,
      destinations: this.destinationId,
      userId: this.item.externalUserId ?? "",
    });

    this.item.phone.forEach((phone) => searchParams.append("sourcePhone", phone));

    if (dateRange && dateRange.fromDate && dateRange.toDate) {
      searchParams.set("fromDate", dateRange.fromDate.startOf("day").toISOString());
      searchParams.set("toDate", dateRange.toDate.endOf("day").toISOString());
    }

    const messages = await getJSON({
      urlSuffix: `/message/?${searchParams}`,
      etoken: this.userStore.etoken ?? "",
    }).then((res) => res.json() as GetSidecarMessages);
    if (!messages.success) {
      return [];
    }

    runInAction(() => {
      this.messages = messages.messages.map((m: ReportedMessage & { groupName?: string }) => ({
        chatId: m.chatId,
        isGroupMsg: m.isGroupMsg ? String(m.isGroupMsg) : "false",
        id: m.id,
        msgId: m._id,
        isDeleted: String(m.isDeleted) === "true",
        isDisappearing: String(m.isDisappearing) === "true",
        isEdited: String(m.isEdited) === "true",
        senderId: m.senderId,
        groupName:
          String(m.isGroupMsg) === "true" ? ((m.isSentByMe as any) === "true" || m.isSentByMe === true ? m.receiverName : m.senderName) : null,
        senderName: String(m.isGroupMsg) === "true" ? (m.notifyName ?? m.senderName) : m.senderName || m.senderPhone,
        senderPhoneNumber: String(m.isGroupMsg) === "true" ? m.authorPhone || m.senderPhone : m.senderPhone,
        receiverId: m.receiverId,
        receiverName:
          m.chatType === "status" ? `${getChannelTypeToText(m.channelType ?? "whatsAppWeb")} ${i18n.t("status")}` : m.receiverName || m.receiverPhone,
        receiverPhoneNumber: m.receiverPhone,
        msg: m.text ?? "",
        isSent: (m.isSentByMe as any) === "true" || m.isSentByMe === true,
        datetime: Number(m.timestamp) && new Date(Number(m.timestamp)).toISOString(),
        quotedMsgId: m.quotedMsgId,
        notifyName: m.notifyName,
        status: m.ack,
        latestUpdate: m.latestUpdate,
        file: m.media
          ? {
              id: "",
              fileType: m.type === "ptt" ? "audio" : m.type,
              size: typeof m.media === "string" ? NaN : m.media.size,
              url: (typeof m.media === "string" ? m.media : m.media.fileUrl) ?? "",
              link: (typeof m.media === "string" ? m.media : m.media.fileUrl) ?? "",
              title: typeof m.media === "object" && m.media.fileName ? m.media.fileName : "",
              transcribeText: m.transcriptText ? `🎤 ${m.transcriptText}` : m.text,
              invalid: typeof m.media === "object" ? m.media.invalid : undefined,
            }
          : undefined,
        userEmail: m.userEmail,
        location: m.location,
        chatType: m.chatType,
        isForwarded: String(m.isForwarded) === "true",
      }));
    });
    this.handleResetSelectedGroupOnDateFilterChange();
  }

  async loadMessagesByRange(dateRange: { fromDate: Dayjs | null; toDate: Dayjs | null }) {
    await this.updateMessages(dateRange);
    return this.messages;
  }

  handleFilterMessages(messages: ComponentMessageType[]) {
    if (this.selectedGroup) {
      return this.selectedGroup.messages.filter(
        (message) =>
          !this._selectedGroupFilters["senderName"]?.includes(message.senderName) && message.datetime !== null && (message.file || message.msg !== "")
      );
    }
    return messages.filter(
      (message) =>
        (!this._filters["phone"]?.includes(stripNumberStringToOnlyNumbers(message.receiverPhoneNumber || "")) ||
          !this._filters["phone"]?.includes(stripNumberStringToOnlyNumbers(message.senderPhoneNumber))) &&
        message.datetime !== null &&
        (message.file || message.msg) &&
        message.isGroupMsg === "false"
    );
  }

  async getUserLocale() {
    return getI18nLanguageFromUserSetting(this.userStore.user?.language ?? "ENGLISH");
  }

  private buildPersonalFilterOptions(messages: ComponentMessageType[]) {
    const phonesWithNamesMap: Record<string, string> = {};
    messages.forEach((message) => {
      if (message.senderId) {
        const normalizeSenderPhoneNumber = stripNumberStringToOnlyNumbers(message.senderPhoneNumber);
        const normalizeSenderNameForPhoneCheck = stripNumberStringToOnlyNumbers(message.senderName);
        if (!phonesWithNamesMap[normalizeSenderPhoneNumber] || normalizeSenderPhoneNumber !== normalizeSenderNameForPhoneCheck) {
          phonesWithNamesMap[normalizeSenderPhoneNumber] = message.senderName || normalizeSenderPhoneNumber;
        }
      }

      if (!message.receiverId || !message.receiverPhoneNumber) return;

      const normalizeReceiverPhoneNumber = stripNumberStringToOnlyNumbers(message.receiverPhoneNumber);
      const normalizeReceiverNameForPhoneCheck = stripNumberStringToOnlyNumbers(message.receiverName || "");

      if (!phonesWithNamesMap[normalizeReceiverPhoneNumber] || normalizeReceiverPhoneNumber !== normalizeReceiverNameForPhoneCheck) {
        phonesWithNamesMap[normalizeReceiverPhoneNumber] = message.receiverName || normalizeReceiverPhoneNumber;
      }
    });
    return Object.entries(phonesWithNamesMap)
      .map(([phone, name]) => ({ phone, name }))
      .sort((a, b) => sortByKey(a, b, "name"));
  }

  private buildGroupFilterOptions(messages: ComponentMessageType[]) {
    return uniqBy(messages, "msgId")
      .map((message) => ({ senderName: message.senderName }))
      .sort((a, b) => sortByKey(a, b, "senderName"));
  }

  private buildFilterOptions(messages: ComponentMessageType[] | undefined, isGroup?: boolean) {
    if (!messages) return [];
    const filteredMessages = uniqBy(messages, "msgId").filter(
      (message) =>
        message.datetime !== null &&
        (message.file || message.msg !== "") &&
        ((isGroup && message.isGroupMsg === "true") || (!isGroup && message.isGroupMsg === "false"))
    );
    return isGroup ? this.buildGroupFilterOptions(filteredMessages) : this.buildPersonalFilterOptions(filteredMessages);
  }

  get filterOptions() {
    if (!this.messages) return [];
    return this.buildFilterOptions(this.messages);
  }

  set filters(val: Record<string, string[]>) {
    this._filters = val;
  }

  get filters() {
    return this._filters;
  }

  set selectedGroupFilters(val: Record<string, string[]>) {
    this._selectedGroupFilters = val;
  }

  get selectedGroupFilters() {
    return this._selectedGroupFilters;
  }

  private showXMonthsAgoToDateRange(showXMonthsAgo: DateFilterMonthsOptions) {
    if (!this.enableDatesFilter) return undefined;
    const fromDate = dayjs().subtract(Number(showXMonthsAgo), "month");
    const toDate = dayjs();
    return { fromDate, toDate };
  }

  async loadMessages(showXMonthsAgo: DateFilterMonthsOptions) {
    const dateRange = this.showXMonthsAgoToDateRange(showXMonthsAgo);
    await this.updateMessages(dateRange);
    return this.messages;
  }

  async getUserId() {
    return this.userStore.user?.userId ?? "";
  }
  get userId() {
    return this.userStore.user?.userId;
  }
  getEntityUrl() {
    return undefined as unknown as string;
  }
  get crm() {
    return undefined as unknown as any;
  }

  onLeaveGroup() {
    this.selectedGroup = undefined;
  }

  async getGroups() {
    const groupsMap: Map<string, ChatGroupType & GroupType> = new Map([]);
    const groupsMessages = this.messages.filter((message) => message?.isGroupMsg === "true");

    const chatIdToGroupName = new Map<string, string>();
    groupsMessages.forEach((m) => {
      if (m.isSent && m.receiverName && m.chatId && !chatIdToGroupName.has(m.chatId)) {
        chatIdToGroupName.set(m.chatId, m.receiverName);
      }
    });

    groupsMessages.forEach((groupMessage) => {
      if (!groupMessage.senderId || (groupMessage.msg === "" && !groupMessage.file)) return;
      const groupId = groupMessage.isSent ? groupMessage.receiverId : groupMessage.senderId;
      if (!groupId) return;

      // For outgoing messages (isSent=true): use groupName directly (reliable)
      // For incoming messages (isSent=false): detect if bug is present
      //   - If notifyName === senderName: bug present (both are person's name), use map
      //   - If notifyName !== senderName: bug not present (senderName has group name), use it
      const consistentGroupName = groupMessage.isSent
        ? groupMessage.groupName
        : groupMessage.senderName === groupMessage.notifyName
          ? groupMessage.chatId
            ? (chatIdToGroupName.get(groupMessage.chatId) ?? groupMessage.groupName)
            : groupMessage.groupName
          : groupMessage.groupName;

      const groupInMap = groupsMap.get(groupId);
      const formattedGroupMessageForUi = {
        ...groupMessage,
        receiverName: undefined,
        senderName: groupMessage.senderName !== consistentGroupName ? groupMessage.senderName : (groupMessage.senderPhoneNumber ?? "N/A"),
      };
      if (groupInMap) {
        groupInMap.messages.push(formattedGroupMessageForUi);
      } else {
        if (!consistentGroupName) return;
        groupsMap.set(groupId, {
          Id: groupId,
          name: consistentGroupName,
          messages: [formattedGroupMessageForUi],
          participants: 0,
          numberOfMessages: 1,
        });
      }
    });

    return {
      groups: Array.from(groupsMap.values()).sort((a, b) => sortByKey(a, b, "name")),
      allowSyncGroups: true,
    };
  }

  get selectedGroupFilterOptions() {
    if (!this.selectedGroup?.messages) return [];
    return this.buildFilterOptions(this.selectedGroup.messages, true);
  }

  private setSelectedGroup(group: GroupType | undefined) {
    this.selectedGroup = group;
    this.selectedGroupFilters = {};
  }

  async getGroup(
    monthsAgoToShow: DateFilterMonthsOptions,
    id?: string
  ): Promise<{
    group:
      | {
          members?: GroupMember[];
          messages: ComponentMessageType[];
          id: string;
          name: string;
        }
      | undefined;
  }> {
    const groups = await this.getGroups();
    const group = groups.groups.find((group) => group.Id === id);
    if (!group) return { group: undefined };
    this.setSelectedGroup(group);
    return {
      group: {
        messages: group?.messages,
        id: group.Id,
        name: group.name,
      },
    };
  }

  async getIsDeleteAllowed() {
    return false;
  }
}
const c = new Container({ defaultScope: "Singleton" });
c.bind(ChatMessagesDataLayerSymbol).to(SidecarMessagesStore);
c.bind(ChatMessagesStore).toSelf();
c.bind(GroupsTabStore).toSelf();
let container: Container | undefined;

const FilterComponent = observer(() => {
  const store = useDependencyInjector<SidecarMessagesStore>(ChatMessagesDataLayerSymbol);
  const chatMessagesStore = useDependencyInjector(ChatMessagesStore);
  const { t } = useTranslation();
  const [searchText, setSearchText] = useState("");

  return (
    <EntityFilter
      textEllipsis={true}
      collapsibleMenuClassName={styles.collapsibleMenu}
      title={t("chat_component.filters_title")}
      currentUserId={store?.userId}
      filterSettings={[
        {
          title: t("filters.sent_or_received_by"),
          maskMe: true,
          searchKey: "phone",
          searchLabel: "name",
        },
      ]}
      useSearchInput={() => ({
        searchText,
        setSearchText,
      })}
      entities={store.filterOptions}
      EntityFilterButton={ChatEntityFilterButton}
      onSelectedFiltersChanged={(selected) => {
        chatMessagesStore.setFilters(selected);
      }}
      disabled={
        !(chatMessagesStore.messages.state === "fulfilled" && chatMessagesStore.messages.value.length > 0) ||
        chatMessagesStore.userId.state !== "fulfilled"
      }
    />
  );
});

const GroupFilterComponent = observer(() => {
  const store = useDependencyInjector<SidecarMessagesStore>(ChatMessagesDataLayerSymbol);
  const chatMessagesStore = useDependencyInjector(ChatMessagesStore);
  const { t } = useTranslation();
  const [searchText, setSearchText] = useState("");

  return (
    <EntityFilter
      textEllipsis={true}
      collapsibleMenuClassName={styles.collapsibleMenu}
      title={t("chat_component.filters_title")}
      currentUserId={store.userId}
      filterSettings={[
        {
          title: t("filters.sent_or_received_by"),
          maskMe: true,
          searchKey: "senderName",
          searchLabel: "senderName",
        },
      ]}
      useSearchInput={() => ({
        searchText,
        setSearchText,
      })}
      entities={store.selectedGroupFilterOptions}
      EntityFilterButton={ChatEntityFilterButton}
      onSelectedFiltersChanged={(selected) => {
        store.selectedGroupFilters = selected;
      }}
      disabled={
        !(chatMessagesStore.messages.state === "fulfilled" && chatMessagesStore.messages.value.length > 0) ||
        chatMessagesStore.userId.state !== "fulfilled"
      }
    />
  );
});

export const ViewMessagesModal: React.FC<
  IModalInstance & { item: ViewMessageItem; destinationId: string; enableDatesFilter?: boolean; limitDateRange?: number }
> = observer(({ closeModal, item, destinationId, enableDatesFilter, limitDateRange }) => {
  container ??= Container.merge(c, mainContainer) as unknown as Container;
  const store = container.get<SidecarMessagesStore>(ChatMessagesDataLayerSymbol);

  useEffect(() => {
    store.init(item, destinationId, enableDatesFilter);
  }, [store, item, destinationId, enableDatesFilter]);

  useEffect(() => () => (container = undefined), []);

  return (
    <DependencyInjectionProvider container={container}>
      <MessageModal closeModal={closeModal} className={styles.messagesModal} closeOnClickOutside={false} bodyClassName={styles.body}>
        <ChatComponent
          hideDateFilter={!enableDatesFilter}
          enableDateRangeFilter={enableDatesFilter}
          allowedToDownload={true}
          FilterComponent={FilterComponent}
          GroupFilterComponent={GroupFilterComponent}
          isLoadingBasedOn={"personal"}
          hideParticipants
          isSidecarsComponent={true}
          limitDateRange={limitDateRange}
        />
      </MessageModal>
    </DependencyInjectionProvider>
  );
});
